import { getIsFreeShipping } from '@cart-services/core/shippingService'
import { useWpCartCalculate } from '@cart-services/hooks/useWpCartCalculate'
import { formatShippingPromotionDescription } from '@cart-services/utils/formatShippingPromotionDescription'
import { type CountryCode, type ProjectCode } from '@constants'
import { useTimeout } from '@dni-react-hooks/use-timeout'
import { Fade } from '@pets-ui/Fade/Fade'
import { HStack } from '@pets-ui/HStack/HStack'
import { Check } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { wpCartInstance } from '../../i18n/wpCartInstance'

export interface ShippingPromoteDescriptionProps {
  projectCode: ProjectCode
  countryCode: CountryCode
  minShippingAmount?: number
  minShippingFee?: number
  currency?: string
  showRemainingAmount?: boolean
  isFixedFee?: boolean
}

export const ShippingPromoteDescription = (props: ShippingPromoteDescriptionProps) => {
  const [isShowFreeShippingNotification, setIsShowFreeShippingNotification] = useState(false)
  const { minShippingAmount, minShippingFee, showRemainingAmount = false, projectCode, countryCode, isFixedFee = false } = props
  const { t } = useTranslation('wpCart', { i18n: wpCartInstance })
  const { calculateResult } = useWpCartCalculate(projectCode, countryCode)
  const { shipping_total } = calculateResult || {}
  const isFreeShipping = getIsFreeShipping(minShippingAmount, minShippingFee, shipping_total)
  /** 已最後門檻但仍有運費 */
  const isShippingDiscount = (minShippingAmount ?? 0) <= 0 && (minShippingFee ?? 0) > 0 && !isFixedFee

  const hasReachedThreshold = isFreeShipping || isShippingDiscount

  const { getShippingPromoteDescriptionParts } = formatShippingPromotionDescription({ t: wpCartInstance.t })
  useEffect(() => {
    if (hasReachedThreshold) {
      setIsShowFreeShippingNotification(true)
    }
  }, [hasReachedThreshold, minShippingAmount, minShippingFee])

  useTimeout(() => {
    if (isShowFreeShippingNotification) {
      setIsShowFreeShippingNotification(false)
    }
  }, (isShowFreeShippingNotification && isFreeShipping) ? 5000 : null)

  if (typeof minShippingAmount === 'undefined' || typeof minShippingFee === 'undefined' || typeof shipping_total === 'undefined') {
    return null
  }

  const { shippingAmountGap, nextShippingText } = getShippingPromoteDescriptionParts(minShippingAmount, minShippingFee)

  if (!hasReachedThreshold) {
    if (showRemainingAmount && !isFixedFee) {
      return (
        <HStack gap={1}>
          <Trans
            i18n={wpCartInstance}
            i18nKey="wpCart.moreToGo"
            defaults="<Text>還差</Text><Gap>{{gap}}</Gap><Text>{{nextShipping}}</Text>"
            values={{ gap: shippingAmountGap, nextShipping: nextShippingText }}
            components={{
              Text: <Text data-testid="text-remaining-free-shipping-amount" color={colors.grey600} />,
              Gap: <Text data-testid="text-remaining-free-shipping-amount" color={colors.red600} />,
            }}
          />
        </HStack>
      )
    }
    return null
  }

  return (
    <Fade
      in={isShowFreeShippingNotification}
      duration={500}
    >
      <HStack alignItems="center" gap={1}>
        <Check color={colors.green600} />
        <Text color={colors.grey600}>
          {isFreeShipping
            ? t('wpCart.noShippingFee', { defaultValue: '達免運' })
            : t('wpCart.shippingDiscountReached', { defaultValue: '達運費折扣' })}
        </Text>
      </HStack>
    </Fade>
  )
}
